import React from 'react';
import { Card, Alert, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../reducers';
import { importAdoptionFile } from '../../../actions/receiver/adoption';
import { ImportTypes } from '../../../../types/import_export';
import RequirementList from '../../common/RequirementList';
import UploadField from '../import_export/UploadField';
import { FILE_TYPE_IDS } from '../../../constants/ImportExportConstants';
import { setFileTypeId } from '../../../actions/parent/exportImport/fetch';
import { AsyncDispatch } from '../../../../types/global';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Title, Text } = Typography;

const AdoptionImportTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(setFileTypeId(FILE_TYPE_IDS.ADOPTION_IMPORT));
  }, [dispatch]);

  const requirementKeys = [
    'requirement1',
    'requirement2',
    'requirement3',
    'requirement4',
    'requirement5',
    'requirement6',
    'requirement7',
  ];

  const { adoptionImport, isImportRunning } = useSelector((state: ApplicationState) => {
    const adoptionImport = state.receiver.receivers.adoptionImport;
    return {
      adoptionImport,
      isImportRunning: adoptionImport.loading,
    };
  });

  const readFile = async (event: any, newFile: File | null) => {
    const file = event.target?.files?.[0] || newFile;

    if (!file) {
      message.error(t('receiver:adoptionImport.pleaseSelectFile'));
      return;
    }

    // Validate file type
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isExcel) {
      message.error(t('receiver:adoptionImport.onlyExcelFiles'));
      return;
    }

    // Validate file size
    const fileSizeInMB = file.size / 1024 / 1024;
    if (fileSizeInMB > 50) {
      message.error(t('receiver:adoptionImport.fileTooLarge'));
      return;
    }

    try {
      await (dispatch as any)(importAdoptionFile(file));
      message.success(t('receiver:adoptionImport.importStarted'));
    } catch (error: any) {
      if (error.response?.status === 404 && error.response?.data?.required_permission) {
        message.error(
          t('receiver:adoptionImport.permissionRequired', {
            permission: error.response.data.required_permission,
          })
        );
      } else if (error.response?.status === 422) {
        message.error(t('receiver:adoptionImport.importAlreadyRunning'));
      } else {
        message.error(error.response?.data?.message || t('receiver:adoptionImport.importFailed'));
      }
    }
  };

  return (
    <div className="adoption-import-tab w-full h-full p-6">
      <Card title={t('receiver:adoptionImport.title')} className="w-full" size="default">
        {adoptionImport.error && (
          <Alert
            message={t('receiver:adoptionImport.error')}
            description={adoptionImport.error}
            type="error"
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        <div className="mb-4">
          <Title level={5}>{t('receiver:adoptionImport.instructions')}</Title>
          <Text type="secondary">{t('receiver:adoptionImport.fileRequirements')}</Text>
          <RequirementList
            requirements={requirementKeys.map(key => t(`receiver:adoptionImport.${key}`))}
          />
        </div>

        <UploadField
          fileType={ImportTypes.EXCEL}
          name={t('receiver:adoptionImport.import')}
          inactive={isImportRunning}
          readFile={readFile}
          canUploadFile
        />
      </Card>
    </div>
  );
};

export default AdoptionImportTab;
